import React, { FC, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { StatsResponse } from '../../models/stats.models';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { buildBarSegments } from '../period-summary/period-summary.helpers';
import { getSportLayerColor } from '../../pages/training-log/training-log.helpers';
import {
  buildPeriodSportChartData,
  buildSeasonSportDistribution,
  formatDistributionPercent,
  formatSportChartValue,
  formatYAxisTick,
  getSportKeysFromPeriodBreakdown,
  getStackedSportYDomain,
  hasSportData,
  MetricMode,
  SportDistributionItem,
  StackedSportChartRow,
} from './sport-breakdown.helpers';
import './sport-breakdown.component.css';

interface SportBreakdownProps {
  stats: StatsResponse | null;
  loading: boolean;
}

interface MetricToggleProps {
  value: MetricMode;
  onChange: (mode: MetricMode) => void;
}

const MetricToggle: FC<MetricToggleProps> = ({ value, onChange }) => (
  <div className="sb-toggle">
    <button type="button" className={value === 'duration' ? 'active' : ''} onClick={() => onChange('duration')}>
      Duration
    </button>
    <button type="button" className={value === 'distance' ? 'active' : ''} onClick={() => onChange('distance')}>
      Distance
    </button>
  </div>
);

interface SportLegendProps {
  sportKeys: string[];
  showPercent?: boolean;
  distribution?: SportDistributionItem[];
}

const SportLegend: FC<SportLegendProps> = ({ sportKeys, showPercent, distribution }) => (
  <div className="sb-legend">
    {sportKeys.map((sportKey, index) => {
      const distItem = distribution?.find((item) => item.key === sportKey);
      const color = distItem?.color ?? getSportLayerColor(sportKey, index);

      return (
        <div className="sb-legend-item" key={sportKey}>
          <div className="sb-legend-dot" style={{ background: color }} />
          {showPercent && distItem ? `${sportKey} · ${formatDistributionPercent(distItem.percent)}` : sportKey}
        </div>
      );
    })}
  </div>
);

interface SportStackedBarChartProps {
  data: StackedSportChartRow[];
  sportKeys: string[];
  mode: MetricMode;
  height: number;
  distanceUnit: string;
}

const SportStackedBarChart: FC<SportStackedBarChartProps> = ({ data, sportKeys, mode, height, distanceUnit }) => {
  const yDomain = useMemo(() => getStackedSportYDomain(data, sportKeys, mode), [data, sportKeys, mode]);

  if (data.length === 0 || sportKeys.length === 0) {
    return <div className="sb-empty">No data</div>;
  }

  return (
    <div className="sb-chart-container" style={{ height }}>
      <StableResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 10, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value) => formatYAxisTick(value, mode, distanceUnit)}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) {
                return null;
              }

              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#111827' }}>{label}</div>
                  {payload
                    .filter((entry) => Number(entry.value) > 0)
                    .map((entry) => {
                      const sportKey = String(entry.dataKey);
                      return (
                        <div key={sportKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: entry.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: '#6B7280', minWidth: 24 }}>{sportKey}</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>
                            {formatSportChartValue(Number(entry.value), mode, distanceUnit)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
          {sportKeys.map((sportKey, index) => (
            <Bar
              key={sportKey}
              dataKey={sportKey}
              stackId="sports"
              fill={getSportLayerColor(sportKey, index)}
              isAnimationActive={false}
              radius={index === sportKeys.length - 1 ? [2, 2, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </StableResponsiveContainer>
    </div>
  );
};

interface SportDistributionSectionProps {
  data: SportDistributionItem[];
  mode: MetricMode;
  onModeChange: (mode: MetricMode) => void;
}

const SportDistributionSection: FC<SportDistributionSectionProps> = ({ data, mode, onModeChange }) => {
  const barSegments = useMemo(
    () =>
      buildBarSegments(
        data.map((item) => item.rawValue),
        data.map((item) => item.color)
      ),
    [data]
  );

  const subtitle = mode === 'duration' ? 'Time spent by sport' : 'Distance by sport';

  if (data.every((item) => item.rawValue === 0)) {
    return (
      <div className="sb-distribution">
        <div className="sb-section-header">
          <div>
            <div className="sb-section-title">Sport Distribution — Yearlong</div>
            <div className="sb-subtitle">{subtitle}</div>
          </div>
          <MetricToggle value={mode} onChange={onModeChange} />
        </div>
        <div className="sb-empty">No sport data</div>
      </div>
    );
  }

  return (
    <div className="sb-distribution">
      <div className="sb-section-header">
        <div>
          <div className="sb-section-title">Sport Distribution — Yearlong</div>
          <div className="sb-subtitle">{subtitle}</div>
        </div>
        <MetricToggle value={mode} onChange={onModeChange} />
      </div>
      <SportLegend sportKeys={data.map((item) => item.key)} showPercent distribution={data} />
      {barSegments.length > 0 && (
        <div className="sb-distribution-bar">
          {barSegments.map((segment, index) => (
            <span key={index} style={{ background: segment.color, flex: segment.flex }} title={data[index]?.label} />
          ))}
        </div>
      )}
    </div>
  );
};

const SportBreakdown: FC<SportBreakdownProps> = ({ stats, loading }) => {
  const [periodMetric, setPeriodMetric] = useState<MetricMode>('duration');
  const [distributionMetric, setDistributionMetric] = useState<MetricMode>('duration');

  const distanceUnit = getMeasurementUnit();

  const sportKeys = useMemo(
    () => getSportKeysFromPeriodBreakdown(stats?.periodBreakdown ?? [], stats?.sportBreakdown ?? {}),
    [stats?.periodBreakdown, stats?.sportBreakdown]
  );

  const periodData = useMemo(
    () => buildPeriodSportChartData(stats?.periodBreakdown ?? [], sportKeys, periodMetric),
    [stats?.periodBreakdown, sportKeys, periodMetric]
  );

  const distributionData = useMemo(
    () => buildSeasonSportDistribution(stats?.sportBreakdown ?? {}, sportKeys, distributionMetric),
    [stats?.sportBreakdown, sportKeys, distributionMetric]
  );

  const periodSubtitle =
    periodMetric === 'duration' ? 'Hours by sport per mesocycle' : `Distance by sport per mesocycle (${distanceUnit})`;

  if (loading) {
    return (
      <div className="sb-card">
        <div className="sb-header">
          <div>
            <div className="sb-title">Sport by Period</div>
            <div className="sb-subtitle">Hours by sport per mesocycle</div>
          </div>
        </div>
        <div className="sb-loading">
          <div className="sb-skeleton sb-skeleton--chart" />
          <div className="sb-skeleton sb-skeleton--bar" />
        </div>
      </div>
    );
  }

  if (!hasSportData(stats)) {
    return (
      <div className="sb-card">
        <div className="sb-header">
          <div>
            <div className="sb-title">Sport by Period</div>
            <div className="sb-subtitle">Hours by sport per mesocycle</div>
          </div>
        </div>
        <div className="sb-empty">No sport data for this season</div>
      </div>
    );
  }

  return (
    <div className="sb-card">
      <div className="sb-header">
        <div>
          <div className="sb-title">Sport by Period</div>
          <div className="sb-subtitle">{periodSubtitle}</div>
        </div>
        <MetricToggle value={periodMetric} onChange={setPeriodMetric} />
      </div>

      <SportLegend sportKeys={sportKeys} />
      <SportStackedBarChart
        data={periodData}
        sportKeys={sportKeys}
        mode={periodMetric}
        height={320}
        distanceUnit={distanceUnit}
      />

      <SportDistributionSection
        data={distributionData}
        mode={distributionMetric}
        onModeChange={setDistributionMetric}
      />
    </div>
  );
};

export default SportBreakdown;
